import { Accordion, CopyToClipboard, InputDescription, TextInput } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { SrtStreamInfo } from '../../../../components/srt-stream-info';
import { IngestProtocol, useGetWebcastSetupSummaryQuery } from '../../../../generated/graphql-manager';
import { getLanguageKey } from '../../../../utils';

import styles from './setup-summary.module.css';

export function EncoderInfo() {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();

  const { data } = useGetWebcastSetupSummaryQuery({ variables: { webcastId }, skip: !webcastId });
  const { primaryLang, additionalLangs, streaming } = data?.webcast || {};
  const ingestProtocol = streaming?.live?.ingestProtocol;
  const streams = streaming?.live?.streams;
  const primaryStream = streams ? streams[0] : undefined;
  const { primaryServer, backupServer, streamName, username, password } = primaryStream?.ingest || {};
  const hasRtmpProtocol = ingestProtocol === IngestProtocol.RTMP;
  const hasSrtProtocol = ingestProtocol === IngestProtocol.SRT;
  const isMultipleLanguages = Boolean(additionalLangs?.length);
  const multipleLanguages = [primaryLang, ...(additionalLangs || [])];
  const streamingParameters = data?.webcast?.streaming?.live?.parameters;
  const rtmpAuthenticationEnabled =
    streamingParameters?.__typename === 'RtmpLiveStreamingParameters' && streamingParameters?.authenticationEnabled;

  let label = '';
  if (hasRtmpProtocol) label = t('manager.webcastSetup.protocolSettings.options.rtmps.label');
  if (hasSrtProtocol) label = t('manager.webcastSetup.protocolSettings.options.srt.label');

  return (
    <Accordion
      data-testid="encoder-info-accordion"
      buttonContent={
        <div className={styles.accordionLabel}>
          <span>{t('manager.webcastSetup.summary.encoder.title')}</span>
          <span data-testid="encoder-info-label">{label}</span>
        </div>
      }
    >
      {hasRtmpProtocol && (
        <>
          <span className={styles.sectionTitle}>{t('manager.webcastSetup.summary.encoder.streamingServer.title')}</span>

          {primaryServer && (
            <InputDescription
              inputId="server-url"
              label={t('manager.webcastSetup.summary.encoder.streamingServer.serverUrlLabel')}
            >
              <TextInput readOnly value={primaryServer} data-testid="server-url">
                <CopyToClipboard value={primaryServer} notificationPosition="left" />
              </TextInput>
            </InputDescription>
          )}

          {backupServer && (
            <InputDescription
              inputId="backup-server-url"
              label={t('manager.webcastSetup.summary.encoder.streamingServer.backupServerUrlLabel')}
            >
              <TextInput readOnly value={backupServer} data-testid="backup-server-url">
                <CopyToClipboard value={backupServer} notificationPosition="left" />
              </TextInput>
            </InputDescription>
          )}

          {streamName && !isMultipleLanguages && (
            <InputDescription
              inputId="streaming-key"
              label={t('manager.webcastSetup.summary.encoder.streamingServer.streamingKeyLabel')}
            >
              <TextInput readOnly value={streamName} data-testid="streaming-key">
                <CopyToClipboard value={streamName} notificationPosition="left" />
              </TextInput>
            </InputDescription>
          )}

          {isMultipleLanguages && (
            <span className={styles.sectionTitle}>{t('manager.webcastSetup.summary.encoder.languagesKeys.title')}</span>
          )}

          {isMultipleLanguages &&
            multipleLanguages.map((language) => {
              const streamName = streams?.find((stream) => stream.metadata?.language === language?.code)?.ingest
                .streamName;

              if (!streamName || !language) return null;

              return (
                <InputDescription
                  key={`streaming-key-${language.code}`}
                  inputId={`streaming-key-${language.code}`}
                  label={
                    language.__typename === 'CustomLanguage'
                      ? language.name
                      : t(`common.languages.${getLanguageKey(language.code)}`)
                  }
                >
                  <TextInput readOnly value={streamName} data-testid={`streaming-key-${language.code}`}>
                    <CopyToClipboard value={streamName} notificationPosition="left" />
                  </TextInput>
                </InputDescription>
              );
            })}

          {rtmpAuthenticationEnabled && (
            <>
              <span className={styles.sectionTitle}>
                {t('manager.webcastSetup.summary.encoder.authentication.title')}
              </span>

              {username && (
                <InputDescription
                  inputId="username"
                  label={t('manager.webcastSetup.summary.encoder.authentication.usernameLabel')}
                >
                  <TextInput readOnly value={username} data-testid="username">
                    <CopyToClipboard value={username} notificationPosition="left" />
                  </TextInput>
                </InputDescription>
              )}

              {password && (
                <InputDescription
                  inputId="password"
                  label={t('manager.webcastSetup.summary.encoder.authentication.passwordLabel')}
                >
                  <TextInput readOnly value={password} data-testid="password">
                    <CopyToClipboard value={password} notificationPosition="left" />
                  </TextInput>
                </InputDescription>
              )}
            </>
          )}
        </>
      )}

      {hasSrtProtocol && <SrtStreamInfo streams={streams} languages={multipleLanguages} />}
    </Accordion>
  );
}
